import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import {
  ProcessOrderProps,
  ProcessOrderFilterType,
  PartyOptionProps,
  SubProcessOptionProps,
  ItemOptionProps,
  ItemSizeOptionProps,
  ItemCuttingSizeOptionProps,
  MaterialOptionProps,
  MaterialSizeOptionProps,
  ProcessOrderLeftoverPoolProps,
} from "../../types/processOrder";
import { formatSize } from "../../lib/utils";

export interface ProcessOrdersState {
  orders: ProcessOrderProps[];
  totalOrders: number;
  filter: ProcessOrderFilterType;
  editOrder: ProcessOrderProps | null;
  viewOrder: ProcessOrderProps | null;
  partyOptions: PartyOptionProps[];
  subProcessOptions: SubProcessOptionProps[];
  itemOptions: ItemOptionProps[];
  itemSizeOptions: ItemSizeOptionProps[];
  // Cutting only — remembered cutting size per (item_id, item_size_id) pair.
  itemCuttingSizeOptions: ItemCuttingSizeOptionProps[];
  // Cascading raw-material picker: pick a Material, then a Material Size
  // scoped to it (materialSizeOptions is refetched per chosen material_id).
  materialOptions: MaterialOptionProps[];
  materialSizeOptions: MaterialSizeOptionProps[];
  leftovers: ProcessOrderLeftoverPoolProps[];
  loading: boolean;
  error: string | null;
}

const initialState: ProcessOrdersState = {
  orders: [],
  totalOrders: 0,
  filter: {
    search: null,
    party_id: null,
    item_id: null,
    fromDate: null,
    toDate: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  editOrder: null,
  viewOrder: null,
  partyOptions: [],
  subProcessOptions: [],
  itemOptions: [],
  itemSizeOptions: [],
  itemCuttingSizeOptions: [],
  materialOptions: [],
  materialSizeOptions: [],
  leftovers: [],
  loading: false,
  error: null,
};

type FetchListProps = {
  process_definition_id: number;
  page: number;
  size?: number;
  search?: string | null;
  party_id?: number | null;
  item_id?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchProcessOrdersList = createAsyncThunk(
  "processOrders/fetchList",
  async (req: FetchListProps) => {
    let response: any;
    await GET(`/v1/process-orders`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderParties = createAsyncThunk(
  "processOrders/fetchParties",
  async (process_definition_id: number) => {
    let response: any;
    await GET(`/v1/process-orders-parties`, { process_definition_id })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderSubProcesses = createAsyncThunk(
  "processOrders/fetchSubProcesses",
  async (process_definition_id: number) => {
    let response: any;
    await GET(`/v1/process-orders-sub-processes`, { process_definition_id })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderItems = createAsyncThunk(
  "processOrders/fetchItems",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-items`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderItemSizes = createAsyncThunk(
  "processOrders/fetchItemSizes",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-item-sizes`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderItemCuttingSizes = createAsyncThunk(
  "processOrders/fetchItemCuttingSizes",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-item-cutting-sizes`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderMaterials = createAsyncThunk(
  "processOrders/fetchMaterials",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-materials`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderMaterialSizes = createAsyncThunk(
  "processOrders/fetchMaterialSizes",
  async (materialId?: number) => {
    let response: any;
    await GET(`/v1/process-orders-material-sizes`, materialId ? { material_id: materialId } : {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderLeftovers = createAsyncThunk(
  "processOrders/fetchLeftovers",
  async (params: { party_id?: number; size_id?: number }) => {
    let response: any;
    await GET(`/v1/process-orders-leftovers`, { ...params })
      .then((res: any) => { response = res; })
      .catch((err: any) => { response = err; });
    return response;
  }
);

export const fetchProcessOrderById = createAsyncThunk(
  "processOrders/fetchById",
  async (id: number) => {
    let response: any;
    await GET(`/v1/process-orders/${id}`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const processOrdersSlice = createSlice({
  name: "processOrders",
  initialState,
  reducers: {
    setEditProcessOrder: (state, action: PayloadAction<ProcessOrderProps | null>) => {
      state.editOrder = action.payload;
    },
    setViewProcessOrder: (state, action: PayloadAction<ProcessOrderProps | null>) => {
      state.viewOrder = action.payload;
    },
    setProcessOrdersFilter: (state, action: PayloadAction<Partial<ProcessOrderFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProcessOrdersList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProcessOrdersList.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.orders = action.payload.data.data.records;
        state.totalOrders = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.orders = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchProcessOrdersList.rejected, (state, action: any) => {
      state.orders = [];
      state.loading = false;
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });
    builder.addCase(fetchProcessOrderParties.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.partyOptions = action.payload.data.data.map((p: any) => ({
          value: p.id,
          label: p.firm_name || p.name,
        }));
      }
    });
    builder.addCase(fetchProcessOrderSubProcesses.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.subProcessOptions = action.payload.data.data.map((p: any) => ({
          value: p.id,
          label: p.name,
          mode_policy: p.mode_policy ?? null,
        }));
      }
    });
    builder.addCase(fetchProcessOrderItems.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemOptions = action.payload.data.data.map((i: any) => ({
          value: i.id,
          label: i.name,
          material_size_id: i.material_id ?? null,
          material_id: i.material_size?.material_id ?? null,
        }));
      }
    });
    builder.addCase(fetchProcessOrderItemSizes.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemSizeOptions = action.payload.data.data.map((s: any) => ({
          value: s.id,
          label: formatSize(s.size),
          size: Number(s.size),
        }));
      }
    });
    builder.addCase(fetchProcessOrderItemCuttingSizes.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemCuttingSizeOptions = action.payload.data.data.map((c: any) => ({
          item_id: c.item_id,
          item_size_id: c.item_size_id,
          cutting_size: Number(c.cutting_size),
        }));
      }
    });
    builder.addCase(fetchProcessOrderMaterials.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.materialOptions = action.payload.data.data.map((m: any) => ({
          value: m.id,
          label: m.name,
        }));
      }
    });
    builder.addCase(fetchProcessOrderMaterialSizes.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.materialSizeOptions = action.payload.data.data.map((s: any) => ({
          value: s.id,
          label: formatSize(s.size),
          size: Number(s.size),
          material_id: s.material_id,
          is_default: !!s.is_default,
        }));
      }
    });
    builder.addCase(fetchProcessOrderLeftovers.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.leftovers = action.payload.data.data;
      }
    });
    builder.addCase(fetchProcessOrderById.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.viewOrder = action.payload.data.data;
      }
    });
  },
});

export const { setEditProcessOrder, setViewProcessOrder, setProcessOrdersFilter } = processOrdersSlice.actions;

export const getProcessOrders = (state: RootState) => state.processOrders.orders;
export const getTotalProcessOrders = (state: RootState) => state.processOrders.totalOrders;
export const getProcessOrdersFilter = (state: RootState) => state.processOrders.filter;
export const getEditProcessOrder = (state: RootState) => state.processOrders.editOrder;
export const getViewProcessOrder = (state: RootState) => state.processOrders.viewOrder;
export const getProcessOrderPartyOptions = (state: RootState) => state.processOrders.partyOptions;
export const getProcessOrderSubProcessOptions = (state: RootState) => state.processOrders.subProcessOptions;
export const getProcessOrderItemOptions = (state: RootState) => state.processOrders.itemOptions;
export const getProcessOrderItemSizeOptions = (state: RootState) => state.processOrders.itemSizeOptions;
export const getProcessOrderItemCuttingSizeOptions = (state: RootState) => state.processOrders.itemCuttingSizeOptions;
export const getProcessOrderMaterialOptions = (state: RootState) => state.processOrders.materialOptions;
export const getProcessOrderMaterialSizeOptions = (state: RootState) => state.processOrders.materialSizeOptions;
export const getProcessOrderLeftovers = (state: RootState) => state.processOrders.leftovers;
export const getProcessOrdersLoading = (state: RootState) => state.processOrders.loading;

export default processOrdersSlice.reducer;
